import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert, Button, Card, Col, DatePicker, Form, Input, Modal, Progress, Row,
  Select, Space, Spin, Table, Tabs, Tag, Typography, Upload, message
} from 'antd';
import {
  BellOutlined, CheckSquareOutlined, DownloadOutlined, EyeOutlined, PaperClipOutlined,
  PlusOutlined, ReloadOutlined, TeamOutlined, UploadOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useWorkbenchContext } from '../../contexts/WorkbenchContext';
import { WorkbenchModal } from './WorkbenchFormPanel';
import * as XLSX from 'xlsx';
import api from '../../services/api';

const { Title, Text } = Typography;
const priorityMeta = {
  normal: { text: '普通', color: 'default' },
  important: { text: '重要', color: 'orange' },
  urgent: { text: '紧急', color: 'red' }
};
const statusText = { draft: '草稿', published: '已发布', archived: '已归档', closed: '已关闭' };
const audienceText = {
  all_teachers: '全体教师', head_teachers: '班主任', grade_teachers: '年级教师', selected_teachers: '指定教师'
};
const audienceOptions = [
  { value: 'head_teachers', label: '班主任' },
  { value: 'all_teachers', label: '全体教师' },
  { value: 'grade_teachers', label: '指定年级教师' },
  { value: 'selected_teachers', label: '指定教师' }
];

const isoValue = (value) => value ? value.toISOString() : null;
const operationId = (value) => {
  const candidate = value && typeof value === 'object' ? (value.id ?? value.taskId ?? value.task_id) : value;
  const id = String(candidate ?? '').trim();
  return /^\d+$/.test(id) ? id : '';
};
const fileUrl = (value) => {
  if (!value || /^https?:\/\//i.test(value)) return value || '#';
  try { return `${new URL(api.defaults.baseURL, window.location.origin).origin}${value}`; }
  catch { return value; }
};
const uploadList = (files) => files.map((file, index) => ({
  uid: file.uid || `${file.url}-${index}`, name: file.originalName, status: 'done', url: fileUrl(file.url), attachment: file
}));

function writeSnapshotWorkbook(snapshot, type, fileName) {
  const source = snapshot.operation || {};
  const workbook = XLSX.utils.book_new();
  const summary = type === 'notice'
    ? [{
        通知标题: source.title, 学校: source.schoolName, 优先级: priorityMeta[source.priority]?.text || source.priority,
        分配范围: audienceText[source.audienceScope] || source.audienceScope, 目标年级: source.targetGrade || '',
        发布人: source.publisherName || '', 发布时间: source.publishedAt || '', 接收人数: (snapshot.recipients || []).length,
        已读人数: (snapshot.recipients || []).filter((item) => Number(item.isRead) === 1).length, 正文: source.content || ''
      }]
    : [{
        任务标题: source.title, 学校: source.schoolName, 优先级: priorityMeta[source.priority]?.text || source.priority,
        分配范围: audienceText[source.assignmentScope] || source.assignmentScope, 目标年级: source.targetGrade || '',
        发布人: source.creatorName || '', 发布时间: source.publishedAt || '', 截止时间: source.dueAt || '',
        分配人数: (snapshot.assignees || []).length,
        完成人数: (snapshot.assignees || []).filter((item) => item.status === 'completed').length, 正文: source.content || ''
      }];
  const details = type === 'notice'
    ? (snapshot.recipients || []).map((item) => ({
        教师: item.teacherName, 账号: item.username, 身份: item.recipientRole, 年级: item.recipientGrade || '',
        阅读状态: Number(item.isRead) === 1 ? '已读' : '未读', 阅读时间: item.readAt || '',
        回执附件: (item.attachments || []).map((file) => file.originalName).join('；')
      }))
    : (snapshot.assignees || []).map((item) => ({
        教师: item.teacherName, 账号: item.username, 状态: item.status, 处理说明: item.progressNote || '',
        开始时间: item.startedAt || '', 完成时间: item.completedAt || '',
        提交附件: (item.attachments || []).map((file) => file.originalName).join('；')
      }));
  const attachments = (snapshot.attachments || []).map((file) => ({
    文件名: file.originalName, 类型: file.mimeType || '', '大小（字节）': file.fileSize || 0, 下载地址: fileUrl(file.url)
  }));
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(summary), '汇总');
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(details), type === 'notice' ? '阅读明细' : '完成明细');
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(attachments), '发布附件');
  XLSX.writeFile(workbook, fileName || `${source.title || type}.xlsx`);
}

export default function SchoolOperations() {
  const workbench = useWorkbenchContext();
  const [entryParams] = useSearchParams();
  const entrySchool = entryParams.get('schoolId'), entryAction = entryParams.get('action');
  const handledEntry = useRef('');
  const [activeTab, setActiveTab] = useState('notices');
  const [loading, setLoading] = useState(true);
  const [workspace, setWorkspace] = useState(null);
  const [schoolId, setSchoolId] = useState('');
  const [notices, setNotices] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [audience, setAudience] = useState({ grades: [], counts: {} });
  const [exports, setExports] = useState([]);
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [taskOpen, setTaskOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [noticeFiles, setNoticeFiles] = useState([]);
  const [taskFiles, setTaskFiles] = useState([]);
  const [taskDetail, setTaskDetail] = useState(null);
  const [noticeDetail, setNoticeDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [noticeForm] = Form.useForm();
  const [taskForm] = Form.useForm();

  const loadWorkspace = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/school-operations/dashboard');
      const data = response.data?.data || {};
      setWorkspace(data);
      const schools = data.schools || [];
      if (workbench && !schools.some(s => String(s.id) === entrySchool)) { setSchoolId(''); return; }
      setSchoolId((current) => String(schools.find(s => String(s.id) === entrySchool)?.id
        || schools.find(s => String(s.id) === current)?.id || data.primarySchool?.id || schools[0]?.id || ''));
    } catch (error) {
      message.error(error.response?.data?.message || '学校工作数据加载失败');
    } finally { setLoading(false); }
  }, [entrySchool, workbench]);

  const loadLists = useCallback(async () => {
    if (!schoolId) return;
    try {
      const results = await Promise.allSettled([
        api.get('/school-operations/notices', { params: { schoolId } }),
        api.get('/school-operations/tasks', { params: { schoolId } }),
        api.get('/school-operations/teachers', { params: { schoolId } }),
        api.get('/school-operations/audience-options', { params: { schoolId } }),
        api.get('/school-operations/exports', { params: { schoolId } })
      ]);

      const [noticeResult, taskResult, teacherResult, audienceResult, exportResult] = results;
      if (noticeResult.status === 'fulfilled') setNotices(noticeResult.value.data?.data || []);
      if (taskResult.status === 'fulfilled') setTasks(taskResult.value.data?.data || []);
      if (teacherResult.status === 'fulfilled') setTeachers(teacherResult.value.data?.data || []);
      if (audienceResult.status === 'fulfilled') setAudience(audienceResult.value.data?.data || { grades: [], counts: {} });
      if (exportResult.status === 'fulfilled') setExports(exportResult.value.data?.data || []);

      const coreFailed = noticeResult.status === 'rejected' || taskResult.status === 'rejected';
      const auxiliaryFailed = results.slice(2).some((result) => result.status === 'rejected');
      if (coreFailed) message.error('通知或任务数据加载失败，已保留页面现有数据');
      else if (auxiliaryFailed) message.warning('通知和任务已加载，部分辅助数据暂时不可用');
    } catch (error) { message.error(error.response?.data?.message || '通知和任务加载失败'); }
  }, [schoolId]);

  useEffect(() => { loadWorkspace(); }, [loadWorkspace]);
  useEffect(() => { loadLists(); }, [loadLists]);
  useEffect(() => {
    setNoticeOpen(false); setTaskOpen(false); setNoticeFiles([]); setTaskFiles([]);
    setNoticeDetail(null); setTaskDetail(null);
    noticeForm.resetFields(); taskForm.resetFields();
  }, [schoolId, noticeForm, taskForm]);
  useEffect(() => {
    const key = `${entrySchool}:${entryAction}`;
    if (loading || workspace?.scopeMode !== 'school_admin' || schoolId !== entrySchool || handledEntry.current === key) return;
    if (!(workspace.schools || []).some(s => String(s.id) === entrySchool)) return;
    if (entryAction === 'notice' || entryAction === 'task') {
      handledEntry.current = key;
      setActiveTab(entryAction === 'notice' ? 'notices' : 'tasks');
      entryAction === 'notice' ? setNoticeOpen(true) : setTaskOpen(true);
    }
  }, [entrySchool, entryAction, schoolId, workspace, loading]);

  const uploadAttachment = (setter) => async ({ file, onSuccess, onError }) => {
    const body = new FormData();
    body.append('file', file);
    try {
      const response = await api.post('/upload', body, { headers: { 'Content-Type': 'multipart/form-data' } });
      const uploaded = Array.isArray(response.data?.data) ? response.data.data[0] : response.data?.data;
      if (!uploaded?.url) throw new Error('上传结果缺少文件地址');
      setter((current) => [...current, uploaded].slice(0, 10));
      onSuccess?.(uploaded);
    } catch (error) {
      message.error(error.response?.data?.message || error.message || '附件上传失败');
      onError?.(error);
    }
  };

  const openDetail = async (type, row) => {
    const id = operationId(row);
    if (!id) return message.error('记录编号无效，请刷新列表后重试');
    type === 'notice' ? setNoticeDetail(row) : setTaskDetail(row);
    setDetailLoading(true);
    try {
      const response = await api.get(`/school-operations/${type === 'notice' ? 'notices' : 'tasks'}/${id}`);
      type === 'notice' ? setNoticeDetail(response.data?.data || null) : setTaskDetail(response.data?.data || null);
    } catch (error) { message.error(error.response?.data?.message || '详情加载失败'); }
    finally { setDetailLoading(false); }
  };

  const createNotice = async (values) => {
    setSaving(true);
    try {
      const response = await api.post('/school-operations/notices', {
        schoolId, title: values.title, content: values.content, priority: values.priority, status: values.status,
        audienceScope: values.audienceScope,
        targetGrade: values.audienceScope === 'grade_teachers' ? values.targetGrade : null,
        targetAccountIds: values.audienceScope === 'selected_teachers' ? (values.targetAccountIds || []) : [],
        expiresAt: isoValue(values.expiresAt), attachments: noticeFiles
      });
      message.success(`通知已保存，接收教师 ${response.data?.data?.recipientCount || 0} 人`);
      setNoticeOpen(false); noticeForm.resetFields(); setNoticeFiles([]); await loadLists();
    } catch (error) { message.error(error.response?.data?.message || '通知保存失败'); }
    finally { setSaving(false); }
  };

  const createTask = async (values) => {
    setSaving(true);
    try {
      const response = await api.post('/school-operations/tasks', {
        schoolId, title: values.title, content: values.content || null, priority: values.priority, status: values.status,
        assignmentScope: values.assignmentScope,
        targetGrade: values.assignmentScope === 'grade_teachers' ? values.targetGrade : null,
        targetAccountIds: values.assignmentScope === 'selected_teachers' ? (values.targetAccountIds || []) : [],
        dueAt: isoValue(values.dueAt), attachments: taskFiles
      });
      message.success(`任务已保存，分配教师 ${response.data?.data?.assigneeCount || 0} 人`);
      setTaskOpen(false); taskForm.resetFields(); setTaskFiles([]); await loadLists();
    } catch (error) { message.error(error.response?.data?.message || '任务保存失败'); }
    finally { setSaving(false); }
  };

  const updateStatus = async (type, id, status) => {
    await api.put(`/school-operations/${type === 'notice' ? 'notices' : 'tasks'}/${id}`, { status });
    message.success('状态已更新');
    await loadLists();
  };

  const exportOperation = async (type, row) => {
    try {
      const response = await api.post('/school-operations/exports', {
        operationType: type, operationId: operationId(row), fileFormat: 'xlsx'
      });
      const data = response.data?.data || {};
      writeSnapshotWorkbook(data.snapshot || {}, type, data.fileName);
      message.success('汇总已导出，并在服务器生成存档');
      await loadLists();
    } catch (error) { message.error(error.response?.data?.message || '汇总导出失败'); }
  };

  const downloadArchive = async (record) => {
    try {
      const response = await api.get(`/school-operations/exports/${record.id}`);
      const data = response.data?.data || {};
      writeSnapshotWorkbook(data.snapshot || {}, data.operationType, data.fileName);
    } catch (error) { message.error(error.response?.data?.message || '存档下载失败'); }
  };

  const audienceField = (fieldName) => (
    <Form.Item noStyle shouldUpdate={(previous, current) => previous[fieldName] !== current[fieldName]}>
      {({ getFieldValue }) => {
        const scope = getFieldValue(fieldName);
        if (scope === 'grade_teachers') return (
          <Form.Item name="targetGrade" label="选择年级" rules={[{ required: true, message: '请选择年级' }]}>
            <Select placeholder="选择一个年级" options={(audience.grades || []).map((grade) => ({ value: grade, label: grade }))} />
          </Form.Item>
        );
        if (scope === 'selected_teachers') return (
          <Form.Item name="targetAccountIds" label="选择教师" rules={[{ required: true, message: '请至少选择一位教师' }]}>
            <Select mode="multiple" showSearch optionFilterProp="label" placeholder="搜索并选择教师" options={teachers.map((item) => ({
              value: String(item.id), label: `${item.name || item.username}${Number(item.isHeadTeacher) ? '（班主任）' : ''}`
            }))} />
          </Form.Item>
        );
        return <Alert showIcon type="info" message={scope === 'head_teachers'
          ? `预计覆盖 ${audience.counts?.headTeachers || 0} 位班主任`
          : `预计覆盖 ${audience.counts?.allTeachers || teachers.length} 位教师`} style={{ marginBottom: 16 }} />;
      }}
    </Form.Item>
  );

  if (loading) return <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>;
  if (workspace?.scopeMode !== 'school_admin') return <Alert type="warning" showIcon message="仅学校管理员可管理学校通知和任务" />;

  const noticeColumns = [
    { title: '标题', dataIndex: 'title', width: 260, render: (value, row) => <Space><Tag color={priorityMeta[row.priority]?.color}>{priorityMeta[row.priority]?.text}</Tag><Text strong>{value}</Text></Space> },
    { title: '分配范围', dataIndex: 'audienceScope', width: 130, render: (value, row) => row.targetGrade ? `${row.targetGrade}教师` : (audienceText[value] || value) },
    { title: '内容', dataIndex: 'content', ellipsis: true },
    { title: '附件', dataIndex: 'attachmentCount', width: 70, render: (value) => Number(value || 0) ? <Tag icon={<PaperClipOutlined />}>{value}</Tag> : '-' },
    { title: '状态', dataIndex: 'status', width: 90, render: (value) => <Tag>{statusText[value] || value}</Tag> },
    { title: '发布时间', dataIndex: 'publishedAt', width: 170, render: (value) => value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-' },
    { title: '操作', width: 260, render: (_, row) => <Space><Button type="link" icon={<EyeOutlined />} onClick={() => openDetail('notice', row)}>详情</Button><Button type="link" icon={<DownloadOutlined />} onClick={() => exportOperation('notice', row)}>导出</Button>{row.status === 'draft' && <Button type="link" onClick={() => updateStatus('notice', row.id, 'published')}>发布</Button>}{row.status !== 'archived' && <Button type="link" danger onClick={() => updateStatus('notice', row.id, 'archived')}>归档</Button>}</Space> }
  ];
  const taskColumns = [
    { title: '任务', dataIndex: 'title', width: 260, render: (value, row) => <Space><Tag color={priorityMeta[row.priority]?.color}>{priorityMeta[row.priority]?.text}</Tag><Text strong>{value}</Text></Space> },
    { title: '分配范围', dataIndex: 'assignmentScope', width: 130, render: (value, row) => row.targetGrade ? `${row.targetGrade}教师` : (audienceText[value] || value) },
    { title: '截止时间', dataIndex: 'dueAt', width: 170, render: (value) => value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '不设截止' },
    { title: '完成进度', width: 220, render: (_, row) => { const total = Number(row.assigneeCount || 0); const done = Number(row.completedCount || 0); return <Space direction="vertical" size={0} style={{ width: '100%' }}><Text>{done} / {total} 人</Text><Progress percent={total ? Math.round(done / total * 100) : 0} size="small" /></Space>; } },
    { title: '状态', dataIndex: 'status', width: 90, render: (value) => <Tag color={value === 'published' ? 'green' : 'default'}>{statusText[value] || value}</Tag> },
    { title: '操作', width: 230, render: (_, row) => <Space><Button type="link" icon={<EyeOutlined />} onClick={() => openDetail('task', row)}>详情</Button><Button type="link" icon={<DownloadOutlined />} onClick={() => exportOperation('task', row)}>导出</Button>{row.status === 'published' && <Button type="link" onClick={() => updateStatus('task', row.id, 'closed')}>关闭</Button>}</Space> }
  ];

  const renderCreationForms = () => <>
<WorkbenchModal title="发布学校通知" open={noticeOpen} onCancel={() => { setNoticeOpen(false); setNoticeFiles([]); }} onOk={() => noticeForm.submit()} confirmLoading={saving} destroyOnClose>
      <Form form={noticeForm} layout="vertical" initialValues={{ priority: 'normal', status: 'published', audienceScope: 'all_teachers' }} onFinish={createNotice}>
        <Form.Item name="title" label="通知标题" rules={[{ required: true, message: '请输入通知标题' }]}><Input maxLength={180} /></Form.Item>
        <Form.Item name="audienceScope" label="分配范围" rules={[{ required: true }]}><Select options={audienceOptions} /></Form.Item>
        {audienceField('audienceScope')}
        <Form.Item name="content" label="通知内容" rules={[{ required: true, message: '请输入通知内容' }]}><Input.TextArea rows={5} maxLength={10000} showCount /></Form.Item>
        <Row gutter={12}><Col span={12}><Form.Item name="priority" label="优先级"><Select options={Object.entries(priorityMeta).map(([value, meta]) => ({ value, label: meta.text }))} /></Form.Item></Col><Col span={12}><Form.Item name="status" label="发布状态"><Select options={[{ value: 'published', label: '立即发布' }, { value: 'draft', label: '保存草稿' }]} /></Form.Item></Col></Row>
        <Form.Item name="expiresAt" label="失效时间（可选）"><DatePicker showTime style={{ width: '100%' }} /></Form.Item>
        <Form.Item label="相关附件（最多10个，单个不超过50MB）"><Upload fileList={uploadList(noticeFiles)} customRequest={uploadAttachment(setNoticeFiles)} onRemove={(file) => setNoticeFiles((items) => items.filter((item) => item.url !== file.attachment?.url))} multiple disabled={noticeFiles.length >= 10}><Button icon={<UploadOutlined />}>上传附件</Button></Upload></Form.Item>
      </Form>
    </WorkbenchModal>

    <WorkbenchModal title="新建学校任务" open={taskOpen} onCancel={() => { setTaskOpen(false); setTaskFiles([]); }} onOk={() => taskForm.submit()} confirmLoading={saving} destroyOnClose>
      <Form form={taskForm} layout="vertical" initialValues={{ priority: 'normal', status: 'published', assignmentScope: 'all_teachers' }} onFinish={createTask}>
        <Form.Item name="title" label="任务标题" rules={[{ required: true, message: '请输入任务标题' }]}><Input maxLength={180} /></Form.Item>
        <Form.Item name="assignmentScope" label="分配范围"><Select options={audienceOptions} /></Form.Item>
        {audienceField('assignmentScope')}
        <Form.Item name="content" label="任务说明"><Input.TextArea rows={4} maxLength={10000} showCount /></Form.Item>
        <Row gutter={12}><Col span={12}><Form.Item name="priority" label="优先级"><Select options={Object.entries(priorityMeta).map(([value, meta]) => ({ value, label: meta.text }))} /></Form.Item></Col><Col span={12}><Form.Item name="dueAt" label="截止时间"><DatePicker showTime style={{ width: '100%' }} /></Form.Item></Col></Row>
        <Form.Item name="status" label="任务状态"><Select options={[{ value: 'published', label: '立即发布' }, { value: 'draft', label: '保存草稿' }]} /></Form.Item>
        <Form.Item label="任务附件（最多10个，单个不超过50MB）"><Upload fileList={uploadList(taskFiles)} customRequest={uploadAttachment(setTaskFiles)} onRemove={(file) => setTaskFiles((items) => items.filter((item) => item.url !== file.attachment?.url))} multiple disabled={taskFiles.length >= 10}><Button icon={<UploadOutlined />}>上传任务文件</Button></Upload></Form.Item>
      </Form>
    </WorkbenchModal>
  </>;

  return <div className={workbench ? 'sw-schooloperations-inline' : undefined}>
    {workbench && renderCreationForms()}
    <Row justify="space-between" align="middle" gutter={[16, 16]} style={{ marginBottom: 20 }}>
      <Col><Title level={2} style={{ margin: 0 }}>学校通知与任务</Title><Text type="secondary">按教师身份或年级精准发布，并沉淀可追溯的处理档案</Text></Col>
      <Col><Space><Select disabled={Boolean(workbench)} value={schoolId} style={{ minWidth: 240 }} onChange={setSchoolId} options={(workspace.schools || []).map((item) => ({ value: String(item.id), label: item.name }))} /><Button icon={<ReloadOutlined />} onClick={loadLists}>刷新</Button></Space></Col>
    </Row>
    <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
      <Col xs={24} md={8}><Card><Space><TeamOutlined style={{ fontSize: 28, color: '#1677ff' }} /><div><Text type="secondary">本校教师</Text><div style={{ fontSize: 24, fontWeight: 600 }}>{teachers.length}</div></div></Space></Card></Col>
      <Col xs={24} md={8}><Card><Space><BellOutlined style={{ fontSize: 28, color: '#fa8c16' }} /><div><Text type="secondary">有效通知</Text><div style={{ fontSize: 24, fontWeight: 600 }}>{notices.filter((item) => item.status === 'published').length}</div></div></Space></Card></Col>
      <Col xs={24} md={8}><Card><Space><CheckSquareOutlined style={{ fontSize: 28, color: '#52c41a' }} /><div><Text type="secondary">进行中任务</Text><div style={{ fontSize: 24, fontWeight: 600 }}>{tasks.filter((item) => item.status === 'published').length}</div></div></Space></Card></Col>
    </Row>
    <Card><Tabs activeKey={activeTab} onChange={setActiveTab} items={[
      { key: 'notices', label: '学校通知', children: <><div style={{ textAlign: 'right', marginBottom: 16 }}><Button type="primary" icon={<PlusOutlined />} onClick={() => setNoticeOpen(true)}>发布通知</Button></div><Table rowKey="id" columns={noticeColumns} dataSource={notices} scroll={{ x: 1100 }} /></> },
      { key: 'tasks', label: '学校任务', children: <><div style={{ textAlign: 'right', marginBottom: 16 }}><Button type="primary" icon={<PlusOutlined />} onClick={() => setTaskOpen(true)}>新建任务</Button></div><Table rowKey="id" columns={taskColumns} dataSource={tasks} scroll={{ x: 1100 }} /></> },
      { key: 'exports', label: `导出存档（${exports.length}）`, children: <Table rowKey="id" dataSource={exports} columns={[
        { title: '档案文件', dataIndex: 'fileName' },
        { title: '类型', dataIndex: 'operationType', width: 100, render: (value) => value === 'notice' ? '通知' : '任务' },
        { title: '记录编号', dataIndex: 'operationId', width: 110 },
        { title: '导出人', dataIndex: 'exporterName', width: 140 },
        { title: '导出时间', dataIndex: 'exportedAt', width: 180, render: (value) => dayjs(value).format('YYYY-MM-DD HH:mm') },
        { title: '操作', width: 110, render: (_, row) => <Button type="link" icon={<DownloadOutlined />} onClick={() => downloadArchive(row)}>重新下载</Button> }
      ]} /> }
    ]} /></Card>

    {!workbench && renderCreationForms()}

    <Modal title="学校任务处理详情" open={Boolean(taskDetail)} onCancel={() => setTaskDetail(null)} footer={null} width={900}>
      <Spin spinning={detailLoading}>{taskDetail?.title && <><Title level={4}>{taskDetail.title}</Title><Text>{taskDetail.content || '无补充说明'}</Text><div style={{ margin: '16px 0' }}><Space wrap>{(taskDetail.attachments || []).map((file) => <Button key={file.id || file.url} size="small" icon={<PaperClipOutlined />} href={fileUrl(file.url)} target="_blank">{file.originalName}</Button>)}</Space></div><Table rowKey="accountId" pagination={false} dataSource={taskDetail.assignees || []} columns={[
        { title: '教师', dataIndex: 'teacherName', width: 150 }, { title: '状态', dataIndex: 'status', width: 100, render: (value) => ({ pending: '待处理', in_progress: '处理中', completed: '已完成' }[value] || value) },
        { title: '完成说明', dataIndex: 'progressNote', render: (value) => value || '-' }, { title: '完成时间', dataIndex: 'completedAt', width: 170, render: (value) => value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-' },
        { title: '完成附件', width: 240, render: (_, row) => <Space wrap>{(row.attachments || []).map((file) => <Button key={file.id || file.url} type="link" size="small" href={fileUrl(file.url)} target="_blank">{file.originalName}</Button>)}</Space> }
      ]} /></>}</Spin>
    </Modal>

    <Modal title="学校通知阅读与回执详情" open={Boolean(noticeDetail)} onCancel={() => setNoticeDetail(null)} footer={null} width={900}>
      <Spin spinning={detailLoading}>{noticeDetail?.title && <><Title level={4}>{noticeDetail.title}</Title><Text>{noticeDetail.content || '无通知正文'}</Text><div style={{ margin: '16px 0' }}><Space wrap>{(noticeDetail.attachments || []).map((file) => <Button key={file.id || file.url} size="small" icon={<PaperClipOutlined />} href={fileUrl(file.url)} target="_blank">{file.originalName}</Button>)}</Space></div><Table rowKey="accountId" pagination={false} dataSource={noticeDetail.readers || []} columns={[
        { title: '教师', dataIndex: 'teacherName', width: 180 }, { title: '状态', dataIndex: 'isRead', width: 90, render: (value) => Number(value) === 1 ? <Tag color="green">已读</Tag> : <Tag>未读</Tag> },
        { title: '阅读时间', dataIndex: 'readAt', width: 180, render: (value) => value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-' },
        { title: '回执附件', render: (_, row) => <Space wrap>{(row.attachments || []).map((file) => <Button key={file.id || file.url} type="link" size="small" href={fileUrl(file.url)} target="_blank">{file.originalName}</Button>)}</Space> }
      ]} /></>}</Spin>
    </Modal>
  </div>;
}
